import { useMemo, useState } from "react";
import { Plus, UserPlus, Sparkle, CreditCard, Coins } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
  DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { fmtCurrency } from "@/lib/format";

const PERFIL_OPTIONS = [
  { value: "Excelente", label: "💎  Excelente — Paga antes de fecha" },
  { value: "Cumplidor", label: "✅  Cumplidor — Paga a tiempo" },
  { value: "Intermitente", label: "⚠️  Intermitente — Requiere recordatorios" },
  { value: "Crítico", label: "🚨  Crítico — Mora recurrente" },
];

const FREQ_OPTIONS = [
  { value: "Diario", label: "Diario · pago diario" },
  { value: "Semanal", label: "Semanal · cada 7 días" },
  { value: "Quincenal", label: "Quincenal · cada 15 días" },
  { value: "Mensual", label: "Mensual · cada 30 días" },
];

const EMPTY_FORM = {
  tipo_registro: "cartera_vencida",
  cliente_nombre: "",
  email: "",
  telefono: "",
  monto_deuda: "",
  dias_vencimiento: "",
  perfil_cumplimiento: "",
  notas_comportamiento: "",
  tipo_cuota: "",
  numero_cuotas: "",
  frecuencia_pago: "",
  cuotas_montos: [],     // array dinámico cuando tipo_cuota === Variable
  detalle_cuotas: "",    // texto libre opcional
};

function SectionTitle({ icon: Icon, title, subtitle }) {
  return (
    <div className="col-span-2 flex items-center gap-2 mt-2">
      <Icon size={16} weight="duotone" className="text-primary" />
      <div>
        <div className="text-sm font-medium">{title}</div>
        {subtitle && <div className="text-xs text-muted-foreground">{subtitle}</div>}
      </div>
    </div>
  );
}

export default function NewDebtorDialog({ onCreate }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  // Sincroniza el array dinámico de cuotas variables con numero_cuotas.
  const variableInputs = useMemo(() => {
    const n = parseInt(form.numero_cuotas) || 0;
    if (form.tipo_cuota !== "Variable" || n <= 0) return [];
    const arr = [...(form.cuotas_montos || [])];
    while (arr.length < n) arr.push("");
    return arr.slice(0, n);
  }, [form.tipo_cuota, form.numero_cuotas, form.cuotas_montos]);

  const updateCuotaMonto = (idx, value) => {
    const next = [...variableInputs];
    next[idx] = value;
    update("cuotas_montos", next);
  };

  // Cálculo automático para Cuota Fija.
  const valorCuotaFija = useMemo(() => {
    const monto = parseFloat(form.monto_deuda);
    const n = parseInt(form.numero_cuotas);
    if (!monto || !n || form.tipo_cuota !== "Fija") return null;
    return Math.round((monto / n) * 100) / 100;
  }, [form.monto_deuda, form.numero_cuotas, form.tipo_cuota]);

  // Suma de cuotas variables (UX: feedback inmediato).
  const sumaVariables = useMemo(() => {
    return variableInputs.reduce((acc, v) => acc + (parseFloat(v) || 0), 0);
  }, [variableInputs]);

  const handleSubmit = async () => {
    if (!form.cliente_nombre.trim()) return toast.error("El nombre del cliente es obligatorio");
    if (!form.telefono.trim()) return toast.error("El teléfono es obligatorio");
    const monto = parseFloat(form.monto_deuda);
    if (!monto || monto <= 0) return toast.error("Ingrese un monto original mayor a cero");

    // Si hay numero_cuotas, exigimos también frecuencia_pago para poder agendar fechas.
    const n = parseInt(form.numero_cuotas) || 0;
    if (n > 0 && !form.frecuencia_pago) {
      return toast.error("Seleccione la frecuencia de pago para generar el cronograma");
    }

    let cuotas_montos = null;
    if (form.tipo_cuota === "Variable" && n > 0) {
      cuotas_montos = variableInputs.map((v) => parseFloat(v) || 0);
      const suma = cuotas_montos.reduce((a, b) => a + b, 0);
      if (Math.abs(suma - monto) > 0.05) {
        return toast.error(`La suma de las cuotas (${fmtCurrency(suma)}) debe igualar el monto original (${fmtCurrency(monto)})`);
      }
    }

    setSaving(true);
    const isVenta = form.tipo_registro === "venta_credito";
    const isPorPagar = form.tipo_registro === "cuenta_por_pagar";
    const payload = {
      tipo_registro: form.tipo_registro,
      cliente_nombre: form.cliente_nombre.trim(),
      email: form.email.trim(),
      telefono: form.telefono.trim(),
      monto_deuda: monto,
      dias_vencimiento: (isVenta || isPorPagar) ? 0 : parseInt(form.dias_vencimiento) || 0,
      perfil_cumplimiento: form.perfil_cumplimiento || null,
      notas_comportamiento: form.notas_comportamiento.trim(),
      tipo_cuota: form.tipo_cuota || null,
      numero_cuotas: n || null,
      frecuencia_pago: form.frecuencia_pago || null,
      detalle_cuotas: form.detalle_cuotas.trim(),
      cuotas_montos,
    };
    const ok = await onCreate(payload);
    setSaving(false);
    if (ok) {
      setForm(EMPTY_FORM);
      setOpen(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-debtor-btn">
          <Plus size={16} weight="bold" className="mr-2" /> Nuevo registro
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="new-debtor-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Nuevo registro</DialogTitle>
          <DialogDescription>
            Seleccione primero el tipo de registro. Los campos marcados con <span className="text-destructive">*</span> son obligatorios.
          </DialogDescription>
        </DialogHeader>

        {/* ── Selector de tipo de registro ────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1" data-testid="tipo-registro-switch">
          {[
            { v: "cartera_vencida", label: "Deudor con cartera vencida", sub: "Deuda existente ya vencida" },
            { v: "venta_credito", label: "Venta a crédito nueva", sub: "Venta realizada hoy a crédito" },
            { v: "cuenta_por_pagar", label: "Cuenta por pagar", sub: "Obligación que tú debes a un proveedor" },
          ].map((opt) => {
            const active = form.tipo_registro === opt.v;
            return (
              <button
                type="button"
                key={opt.v}
                onClick={() => update("tipo_registro", opt.v)}
                data-testid={`tipo-${opt.v}`}
                className={`text-left p-3 rounded-md border transition-all ${
                  active
                    ? "border-primary bg-primary/5 ring-1 ring-primary"
                    : "border-border bg-card hover:border-primary/40"
                }`}
              >
                <div className="text-sm font-medium">{opt.label}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">{opt.sub}</div>
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-2 gap-4 pt-2">
          <SectionTitle icon={UserPlus} title="Datos del cliente" subtitle="Información de contacto y monto" />

          <div className="col-span-2">
            <Label>
              {form.tipo_registro === "cuenta_por_pagar" ? "Proveedor / Razón social" : "Cliente / Razón social"} <span className="text-destructive">*</span>
            </Label>
            <Input data-testid="form-cliente" value={form.cliente_nombre}
              onChange={(e) => update("cliente_nombre", e.target.value)}
              placeholder={form.tipo_registro === "cuenta_por_pagar" ? "Proveedor Ejemplo S.A." : "Distribuidora El Rocío S.A."} />
          </div>

          <div>
            <Label>Email</Label>
            <Input data-testid="form-email" type="email" value={form.email}
              onChange={(e) => update("email", e.target.value)} placeholder="cobros@empresa.ec" />
          </div>

          <div>
            <Label>Teléfono <span className="text-destructive">*</span></Label>
            <Input data-testid="form-telefono" value={form.telefono}
              onChange={(e) => update("telefono", e.target.value)} placeholder="0991234567" />
          </div>

          <div>
            <Label>
              {form.tipo_registro === "venta_credito"
                ? "Monto de la venta"
                : form.tipo_registro === "cuenta_por_pagar"
                ? "Monto a pagar"
                : "Monto vencido"} (USD) <span className="text-destructive">*</span>
            </Label>
            <Input data-testid="form-monto" type="number" step="0.01" min="0"
              value={form.monto_deuda} onChange={(e) => update("monto_deuda", e.target.value)}
              placeholder="1250.75" />
          </div>

          <div>
            <Label>{
              form.tipo_registro === "venta_credito"
                ? "Días desde hoy"
                : form.tipo_registro === "cuenta_por_pagar"
                ? "Días hasta vencer"
                : "Días vencidos"
            }</Label>
            <Input data-testid="form-dias" type="number" min="0"
              value={form.tipo_registro === "venta_credito" ? "0" : form.dias_vencimiento}
              onChange={(e) => update("dias_vencimiento", e.target.value)}
              disabled={form.tipo_registro === "venta_credito"}
              placeholder="0" />
            {form.tipo_registro === "venta_credito" && (
              <p className="text-[11px] text-muted-foreground mt-1">Venta nueva: la cuenta arranca en 0 días.</p>
            )}
            {form.tipo_registro === "cuenta_por_pagar" && (
              <p className="text-[11px] text-muted-foreground mt-1">Días restantes para cumplir con el proveedor.</p>
            )}
          </div>

          <SectionTitle icon={Sparkle} title="Perfil de cumplimiento" subtitle="Clasificación cualitativa del cliente" />

          <div className="col-span-2">
            <Label>Perfil del cliente</Label>
            <Select value={form.perfil_cumplimiento} onValueChange={(v) => update("perfil_cumplimiento", v)}>
              <SelectTrigger data-testid="form-perfil"><SelectValue placeholder="Seleccione un perfil…" /></SelectTrigger>
              <SelectContent>
                {PERFIL_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="col-span-2">
            <Label>Notas de comportamiento</Label>
            <Textarea data-testid="form-notas" value={form.notas_comportamiento}
              onChange={(e) => update("notas_comportamiento", e.target.value)}
              placeholder="Ej: suele pagar los viernes por la tarde, prefiere transferencia, …" rows={2} />
          </div>

          <SectionTitle icon={CreditCard} title="Condiciones de crédito" subtitle="Cuotas pactadas con el cliente" />

          <div>
            <Label>Tipo de cuota</Label>
            <Select value={form.tipo_cuota} onValueChange={(v) => update("tipo_cuota", v)}>
              <SelectTrigger data-testid="form-tipo-cuota"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Fija">Fija (todas iguales)</SelectItem>
                <SelectItem value="Variable">Variable (montos distintos)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Nº de cuotas</Label>
            <Input data-testid="form-numero-cuotas" type="number" min="1"
              value={form.numero_cuotas} onChange={(e) => update("numero_cuotas", e.target.value)}
              placeholder="3" />
          </div>

          <div className="col-span-2">
            <Label>Frecuencia de pago</Label>
            <Select value={form.frecuencia_pago} onValueChange={(v) => update("frecuencia_pago", v)}>
              <SelectTrigger data-testid="form-frecuencia"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {FREQ_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Resumen automático para Cuota Fija */}
          {form.tipo_cuota === "Fija" && valorCuotaFija !== null && (
            <div
              className="col-span-2 flex items-center gap-3 p-3 rounded-md border border-primary/30 bg-primary/5 text-sm"
              data-testid="cuota-fija-preview"
            >
              <Coins size={20} weight="duotone" className="text-primary" />
              <div>
                <div className="font-medium">Valor calculado por cuota</div>
                <div className="text-muted-foreground">
                  {form.numero_cuotas} cuotas × <span className="font-mono font-semibold text-foreground">{fmtCurrency(valorCuotaFija)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Inputs dinámicos para Cuota Variable */}
          {form.tipo_cuota === "Variable" && variableInputs.length > 0 && (
            <div className="col-span-2 space-y-2 rounded-md border border-border p-3 bg-accent/20" data-testid="cuota-variable-grid">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium">Monto por cuota</div>
                <div className="text-xs text-muted-foreground">
                  Suma: <span className="font-mono font-semibold text-foreground">{fmtCurrency(sumaVariables)}</span>
                  {" / "}
                  <span className="font-mono">{fmtCurrency(parseFloat(form.monto_deuda) || 0)}</span>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {variableInputs.map((value, i) => (
                  <div key={i}>
                    <Label className="text-xs text-muted-foreground">Cuota {i + 1}</Label>
                    <Input
                      data-testid={`form-cuota-monto-${i + 1}`}
                      type="number"
                      step="0.01"
                      min="0"
                      value={value}
                      onChange={(e) => updateCuotaMonto(i, e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancelar</Button>
          <Button data-testid="form-submit" onClick={handleSubmit} disabled={saving}>
            {saving ? "Registrando…" : "Registrar deudor"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
